import { BalanceData, GearTarget, Rarity } from "../data/balance";
import { Fix64 } from "../maths/fix64";
import { Fnv1a64 } from "../maths/fnv1a64";
import { Pcg32 } from "../maths/pcg32";
import { ChestResult, isFarmBand } from "../sim/chest";
import { GearItem, SalvageYield, itemPower, mythicEffectsFor, primaryAt, raiseCostAt, raiseCostRange, rollAffixes, salvageYield } from "./gear";
import { PlayerProfile } from "./profile";

/**
 * The forge (gear v2, docs/GEAR_V2.md): raising an item's level with Forge Essence, fusing three items of a rarity into
 * one of the next, and feeding spare items into one. Pure rules over the profile; previews and actions are the same
 * functions, so what a preview shows is what the action does.
 */

/** Pcg32 stream for fusion's new affixes and Mythic effect, apart from every other use of the same seeds. */
const FUSION_STREAM = 431;

// ------------------------------------------------------------------ raising

/**
 * The highest level an item can be raised to: the level of the tower or fortress wearing it; unworn, the highest level
 * among the owners of its kind (the fortress for base gear, the best tower type for tower gear). Never above maxLevel.
 */
export function raiseCap(profile: PlayerProfile, item: GearItem): number {
  const B = profile.balance;
  const kind = B.gear.slots[item.slot].target;
  const top = item.equippedTo !== null ? profile.ownerLevel(item.equippedTo)
    : kind === GearTarget.Base ? profile.baseLevel : Math.max(1, ...profile.towerLevels.filter((_, i) => !B.towers[i].isBase));
  return Math.min(top, B.gear.itemLevel.maxLevel);
}

/** Raises an item one level for Forge Essence; it binds once worn and raised past `bindAboveRaised` levels. */
export function raiseGear(profile: PlayerProfile, itemId: number): true | string {
  const it = profile.findGear(itemId);
  if (!it) return "no such item";
  if (it.level >= raiseCap(profile, it)) return "at its cap";
  const cost = raiseCostAt(profile.balance, it.rarity, it.level);
  if (profile.forge.essence < cost) return "not enough essence";
  profile.forge.essence -= cost;
  it.essenceSpent += cost;
  it.level++;
  it.raised++;
  profile.forge.raisedLevels++;
  if (it.equippedTo !== null && it.raised > profile.balance.gear.bindAboveRaised) it.bound = true;
  return true;
}

/** Raises an item toward `toLevel` as far as essence and its cap allow. Returns the levels gained and the essence spent. */
export function raiseGearTo(profile: PlayerProfile, itemId: number, toLevel: number): { readonly levels: number; readonly essence: number } | string {
  const it = profile.findGear(itemId);
  if (!it) return "no such item";
  const before = profile.forge.essence;
  let levels = 0;
  while (it.level < toLevel && raiseGear(profile, itemId) === true) levels++;
  if (levels === 0) return it.level >= raiseCap(profile, it) ? "at its cap" : it.level >= toLevel ? "already there" : "not enough essence";
  return { levels, essence: before - profile.forge.essence };
}

/** What the item card's forge row shows: the next level's cost and stats, and how far the essence held goes. */
export interface RaisePreview {
  readonly level: number;
  readonly cap: number;
  /** Essence for the next level (0 at the cap). */
  readonly nextCost: number;
  /** Essence from here to the cap. */
  readonly costToCap: number;
  /** The level the essence held now reaches (≤ cap). */
  readonly affordableLevel: number;
  readonly primaryNow: Fix64;
  /** Primary at the next level (= now at the cap). */
  readonly primaryNext: Fix64;
  readonly powerNow: number;
  readonly powerNext: number;
  /** The next raise would bind it (worn, past the threshold, not bound yet). */
  readonly bindsOnNext: boolean;
}

export function raisePreview(profile: PlayerProfile, item: GearItem): RaisePreview {
  const B = profile.balance;
  const cap = Math.max(item.level, raiseCap(profile, item));
  const next = Math.min(item.level + 1, cap);
  let affordable = item.level, left = profile.forge.essence;
  while (affordable < cap && left >= raiseCostAt(B, item.rarity, affordable)) left -= raiseCostAt(B, item.rarity, affordable++);
  return {
    level: item.level, cap,
    nextCost: item.level < cap ? raiseCostAt(B, item.rarity, item.level) : 0,
    costToCap: raiseCostRange(B, item.rarity, item.level, cap),
    affordableLevel: affordable,
    primaryNow: primaryAt(B, item, item.level), primaryNext: primaryAt(B, item, next),
    powerNow: itemPower(B, item), powerNext: itemPower(B, { rarity: item.rarity, level: next }),
    bindsOnNext: !item.bound && item.equippedTo !== null && item.level < cap && item.raised + 1 > B.gear.bindAboveRaised,
  };
}

// ------------------------------------------------------------------ fusion

/**
 * A fusion: the keeper (whose slot, affixes and place the result keeps), the fodder items and how many Fusion Sigils
 * of the rarity stand in for the rest (fodder + sigils = 2). `mythicEffect`: for Legendary → Mythic, the effect to
 * forge in (an index fitting the slot's kind); absent, one is rolled.
 */
export interface FusionRequest {
  readonly keeper: number;
  readonly fodder: readonly number[];
  readonly sigils: number;
  readonly mythicEffect?: number;
}

export interface FusionPlan {
  readonly keeper: GearItem;
  readonly fodder: readonly GearItem[];
  readonly sigils: number;
  /** The item the fusion makes (the keeper's id). */
  readonly result: GearItem;
  /** Essence given back: all the fodder's raise spend, and the levels the keeper's cap cut off. */
  readonly essenceBack: number;
  /** Essence the fusion costs (gear.json fusion.essence by the result's rarity), paid after `essenceBack` comes in. */
  readonly fee: number;
}

/** Items a fusion takes in a kind: tower gear with tower gear, fortress gear with fortress gear. */
export const kindOf = (balance: BalanceData, item: GearItem): GearTarget => balance.gear.slots[item.slot].target;

/** The seed of a fusion's roll: the same request always makes the same item (the preview is the result). */
export function fusionSeed(req: FusionRequest): bigint {
  const h = new Fnv1a64();
  h.writeString("fusion");
  h.writeInt(req.keeper);
  for (const id of [...req.fodder].sort((a, b) => a - b)) h.writeInt(id);
  h.writeInt(req.sigils);
  return h.value;
}

/** Checks a fusion and works out its result without changing anything. A string says why it cannot happen. */
export function planFusion(profile: PlayerProfile, req: FusionRequest): FusionPlan | string {
  const B = profile.balance, G = B.gear;
  const keeper = profile.findGear(req.keeper);
  if (!keeper) return "no such item";
  if (keeper.rarity >= Rarity.Mythic) return "Mythic is the top rarity";
  if (!Number.isInteger(req.sigils) || req.sigils < 0 || req.sigils > G.fusionMaxSigils) return "too many sigils";
  if (req.fodder.length + req.sigils !== 2) return "needs three of a rarity";
  if (profile.forge.sigils[keeper.rarity] < req.sigils) return "not enough sigils";
  const fodder: GearItem[] = [];
  for (const id of req.fodder) {
    const it = profile.findGear(id);
    if (!it || id === keeper.id || fodder.includes(it)) return "no such item";
    if (it.rarity !== keeper.rarity) return "rarities differ";
    if (kindOf(B, it) !== kindOf(B, keeper)) return "tower and fortress gear do not mix";
    if (it.equippedTo !== null) return "fodder is worn";
    if (it.locked) return "fodder is locked";
    fodder.push(it);
  }
  const rarity = (keeper.rarity + 1) as Rarity;
  const kind = kindOf(B, keeper);
  if (req.mythicEffect !== undefined && (rarity !== Rarity.Mythic || !mythicEffectsFor(B, kind).includes(req.mythicEffect))) return "that Mythic effect does not fit";
  const rng = new Pcg32(fusionSeed(req), FUSION_STREAM);
  const slot = G.slots[keeper.slot];
  const affixes = rollAffixes(B, rarity, keeper.affixes, rng);
  let mythicEffect = keeper.mythicEffect;
  if (rarity === Rarity.Mythic) {
    const fits = mythicEffectsFor(B, kind);
    mythicEffect = req.mythicEffect ?? (fits.length > 0 ? fits[rng.nextInt(fits.length)] : -1);
  }
  const best = Math.max(keeper.level, ...fodder.map((f) => f.level));
  const cap = Math.max(keeper.level, raiseCap(profile, keeper));
  const level = Math.min(best, cap);
  const essenceBack = fodder.reduce((n, f) => n + f.essenceSpent, 0) + raiseCostRange(B, keeper.rarity, level, best);
  const fee = G.fusionEssence[rarity] ?? 0;
  if (profile.forge.essence + essenceBack < fee) return "not enough essence";
  const result: GearItem = {
    id: keeper.id, slot: keeper.slot, rarity, primaryStat: slot.primary, primaryBase: G.primaryBase[slot.primary].mul(G.primaryRarityMult[rarity]),
    affixes, mythicEffect, level, raised: keeper.raised, essenceSpent: keeper.essenceSpent,
    bound: keeper.bound || fodder.some((f) => f.bound), locked: keeper.locked, equippedTo: keeper.equippedTo === null ? null : { ...keeper.equippedTo },
  };
  return { keeper, fodder, sigils: req.sigils, result, essenceBack, fee };
}

/** Fuses: the fodder and sigils go, the keeper becomes the result in place (worn where it was), the fodder's essence comes back, the fee is paid. */
export function fuse(profile: PlayerProfile, req: FusionRequest): FusionPlan | string {
  const plan = planFusion(profile, req);
  if (typeof plan === "string") return plan;
  profile.gear[profile.gear.indexOf(plan.keeper)] = plan.result;
  for (const f of plan.fodder) profile.gear.splice(profile.gear.indexOf(f), 1);
  profile.forge.sigils[plan.keeper.rarity] -= plan.sigils;
  profile.forge.essence += plan.essenceBack - plan.fee;
  profile.forge.fused[plan.result.rarity]++;
  return plan;
}

/**
 * Fusion candidates for a keeper: unworn, unlocked items of its rarity and kind, best fodder first (lowest gear power,
 * then lowest level, then oldest) — the items a player would least miss.
 */
export function fusionFodder(profile: PlayerProfile, keeper: GearItem): GearItem[] {
  const B = profile.balance;
  if (keeper.rarity >= Rarity.Mythic) return [];
  return profile.gear
    .filter((it) => it.id !== keeper.id && it.rarity === keeper.rarity && kindOf(B, it) === kindOf(B, keeper) && it.equippedTo === null && !it.locked)
    .sort((a, b) => itemPower(B, a) - itemPower(B, b) || a.level - b.level || a.id - b.id);
}

// ------------------------------------------------------------------ feeding

/** A feed: the fodder salvaged into the purse, then the target raised as far as the essence (and its cap) allow. */
export interface FeedPlan {
  readonly target: GearItem;
  readonly fodder: readonly GearItem[];
  /** What the fodder pays (gold and essence) before the raise spends essence. */
  readonly gold: number;
  readonly essence: number;
  /** The target's level after the feed. */
  readonly toLevel: number;
  /** Essence the raise spends. */
  readonly spent: number;
}

/**
 * Checks a feed. Fodder is never the target, worn, locked or Mythic; Epic and up only with `confirmEpic` (the bulk
 * flows never take them unasked).
 */
export function planFeed(profile: PlayerProfile, targetId: number, fodderIds: readonly number[], confirmEpic = false): FeedPlan | string {
  const B = profile.balance;
  const target = profile.findGear(targetId);
  if (!target) return "no such item";
  const fodder: GearItem[] = [];
  for (const id of fodderIds) {
    const it = profile.findGear(id);
    if (!it || id === targetId || fodder.includes(it)) return "no such item";
    if (it.equippedTo !== null) return "fodder is worn";
    if (it.locked) return "fodder is locked";
    if (it.rarity === Rarity.Mythic) return "Mythic is never fodder";
    if (it.rarity >= B.gear.setMinRarity && !confirmEpic) return "Epic and up need confirmation";
    fodder.push(it);
  }
  const yields: SalvageYield[] = fodder.map((f) => salvageYield(B, f));
  const gold = yields.reduce((n, y) => n + y.gold, 0), essence = yields.reduce((n, y) => n + y.essence, 0);
  const cap = raiseCap(profile, target);
  let level = target.level, left = profile.forge.essence + essence, spent = 0;
  while (level < cap && left >= raiseCostAt(B, target.rarity, level)) { const c = raiseCostAt(B, target.rarity, level++); left -= c; spent += c; }
  return { target, fodder, gold, essence, toLevel: level, spent };
}

export function feed(profile: PlayerProfile, targetId: number, fodderIds: readonly number[], confirmEpic = false): FeedPlan | string {
  const plan = planFeed(profile, targetId, fodderIds, confirmEpic);
  if (typeof plan === "string") return plan;
  for (const f of plan.fodder) profile.salvageGear(f.id);
  raiseGearTo(profile, targetId, plan.toLevel);
  return plan;
}

// ------------------------------------------------------------------ battle chests

/**
 * Forge Essence a level's own battle chest pays (gear v2): essence.chest[rarity] × (1 + level / chestLevelDivisor),
 * × farmMult on a farm run. Paid when the chest is banked, outside the replay hash; purchased chests pay none.
 */
export function chestEssence(balance: BalanceData, chest: ChestResult): number {
  const e = balance.gear.essence, d = Math.max(1, e.chestLevelDivisor);
  const base = Math.floor((e.chest[chest.rarity] * (d + chest.level)) / d);
  return isFarmBand(chest.band) ? e.farmMult.mulInt(base).floorToInt() : base;
}
